import { dateTime, money } from './format.js';

/**
 * The lifecycle audit, written out rather than dumped.
 *
 * This panel used to print `JSON.stringify(entry.data)` - one unbroken line of
 * quotes, braces and camelCase running off the side of the page. Everything was
 * there and none of it was readable, which for an audit trail is close to the
 * worst outcome: it looks like disclosure while functioning as concealment. The
 * one moment a merchant reads this is when something has gone wrong with their
 * money, and that is the worst moment to hand somebody a wall of JSON.
 *
 * So each entry is a heading and a short list of labelled facts. Amounts are
 * money, timestamps are dates, identifiers are identifiers, and a key nobody has
 * named yet is de-camel-cased rather than hidden - a field added later shows up
 * legibly instead of disappearing from the audit trail, which is the failure
 * mode that would actually matter.
 */

/** Actions, as a sentence. Anything unlisted is de-dotted and capitalised. */
const ACTIONS: Record<string, string> = {
  'payment.created': 'Payment created',
  'payment.updated': 'Payment updated',
  'payment.attempt_started': 'Customer started paying',
  'payment.attempt_failed': 'Attempt failed',
  'payment.succeeded': 'Payment received',
  'payment.cancelled': 'Payment cancelled',
  'payment.expired': 'Payment expired',
  'payment.refunded': 'Refund issued',
  'payment.partially_refunded': 'Partial refund issued',
  'payment.disputed': 'Dispute opened',
  'payment.dispute_closed': 'Dispute closed',
  'payment.manual_reconcile': 'Checked against Stripe',
  'payment_link.created': 'Payment link issued',
  'payment_link.renewed': 'Payment link renewed',
  'payment_link.revoked': 'Payment link cancelled',
  'payment_link.used': 'Payment link opened',
  'mail.payment_link_sent': 'Payment link emailed',
};

/** Keys worth naming properly. Everything else is de-camel-cased below. */
const LABELS: Record<string, string> = {
  amount: 'Amount',
  applicationFeeAmount: 'Our fee',
  attemptId: 'Attempt',
  changed: 'Anything changed',
  commandId: 'Command',
  connectedAccountId: 'Stripe account',
  currency: 'Currency',
  discrepancies: 'Discrepancies',
  expiresAt: 'Expires',
  linkId: 'Link',
  merchantId: 'Merchant',
  merchantReference: 'Your reference',
  sequence: 'Attempt number',
  state: 'State',
  stripePaymentIntentId: 'Stripe payment',
  to: 'Sent to',
  ttlSeconds: 'Valid for',
  reason: 'Reason',
};

/** Keys not worth a row: repeated from the page around them, or noise. */
const HIDDEN = new Set(['merchantId', 'connectedAccountId']);

export function actionLabel(action: string): string {
  if (ACTIONS[action]) return ACTIONS[action];
  const words = action.replace(/[._]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function label(key: string): string {
  if (LABELS[key]) return LABELS[key];
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[._]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** True for the long opaque strings that read better in a monospace chip. */
function looksLikeId(value: string): boolean {
  return /^(pi_|ch_|acct_|cus_|mch_|usr_|cmd_|plink_|attempt_|re_|dp_|evt_|in_)/.test(value) || /^[A-Z0-9_]{20,}$/.test(value);
}

function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
}

function seconds(total: number): string {
  if (total % 86_400 === 0) return `${total / 86_400} day${total === 86_400 ? '' : 's'}`;
  if (total % 3_600 === 0) return `${total / 3_600} hour${total === 3_600 ? '' : 's'}`;
  if (total % 60 === 0) return `${total / 60} minutes`;
  return `${total} seconds`;
}

function Value({ k, value, currency }: { k: string; value: unknown; currency: string }) {
  if (value === null || value === undefined) return <span style={{ color: 'var(--wc-faint)' }}>none</span>;

  if (typeof value === 'boolean') return <>{value ? 'Yes' : 'No'}</>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: 'var(--wc-faint)' }}>none</span>;
    return <>{value.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ')}</>;
  }

  if (typeof value === 'number') {
    // Minor units, so they are shown as money rather than as a bare integer.
    if (k === 'amount' || /Amount$/.test(k)) return <>{money(value, currency)}</>;
    if (k === 'ttlSeconds') return <>{seconds(value)}</>;
    return <>{value}</>;
  }

  if (typeof value === 'string') {
    if (isIsoDate(value)) return <>{dateTime(value)}</>;
    if (looksLikeId(value)) {
      return (
        <code
          style={{
            fontFamily: 'var(--wc-mono)',
            fontSize: 11.5,
            background: 'var(--wc-surface-alt)',
            padding: '1px 5px',
            borderRadius: 4,
            wordBreak: 'break-all',
          }}
        >
          {value}
        </code>
      );
    }
    return <>{value}</>;
  }

  /**
   * A nested object, which nothing writes today. Rendered as compact JSON
   * rather than dropped: an audit entry that silently omits part of itself is
   * worse than an ugly one.
   */
  return (
    <code style={{ fontFamily: 'var(--wc-mono)', fontSize: 11.5, wordBreak: 'break-all' }}>
      {JSON.stringify(value)}
    </code>
  );
}

export function TimelineData({ data, currency }: { data: unknown; currency: string }) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

  const entries = Object.entries(data as Record<string, unknown>).filter(([k]) => !HIDDEN.has(k));
  if (entries.length === 0) return null;

  return (
    <dl
      style={{
        margin: '6px 0 0',
        display: 'grid',
        gridTemplateColumns: 'minmax(110px, max-content) 1fr',
        gap: '3px 14px',
        fontSize: 12.5,
        alignItems: 'baseline',
      }}
    >
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt style={{ color: 'var(--wc-faint)' }}>{label(k)}</dt>
          <dd style={{ margin: 0, color: 'var(--wc-muted)', minWidth: 0 }}>
            <Value k={k} value={v} currency={currency} />
          </dd>
        </div>
      ))}
    </dl>
  );
}
